import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, MessageSquare, ShieldCheck } from "lucide-react";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message received — we'll get back to you within 1 business day.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not send message");
    } finally { setLoading(false); }
  };

  const f = "mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl h-12";

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Get in touch</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">Contact us</h1>
      <p className="text-[#5F6C60] mt-3 max-w-2xl leading-relaxed">
        Questions about your listing, a deal, a refund, or a partnership? We reply to every message — usually within 1 business day.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        <div className="bg-white border border-[#E8E2D6] rounded-2xl p-6 flex flex-col">
          <Mail className="w-5 h-5 text-[#D97757]" strokeWidth={1.5} />
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#5F6C60] mt-3">Email</div>
          <a data-testid="contact-email-link" href="mailto:hello@massagesavings.com" className="font-serif-display text-2xl mt-1 hover:text-[#D97757]">hello@massagesavings.com</a>
          <p className="text-xs text-[#5F6C60] mt-2">General questions & partnerships</p>
        </div>
        <div className="bg-white border border-[#E8E2D6] rounded-2xl p-6 flex flex-col">
          <ShieldCheck className="w-5 h-5 text-[#0F766E]" strokeWidth={1.5} />
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#5F6C60] mt-3">Legal & Privacy</div>
          <a href="mailto:legal@massagesavings.com" className="font-serif-display text-xl mt-1 hover:text-[#D97757]">legal@massagesavings.com</a>
          <a href="mailto:privacy@massagesavings.com" className="font-serif-display text-xl mt-1 hover:text-[#D97757]">privacy@massagesavings.com</a>
        </div>
        <div className="bg-white border border-[#E8E2D6] rounded-2xl p-6 flex flex-col">
          <MessageSquare className="w-5 h-5 text-[#D97757]" strokeWidth={1.5} />
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#5F6C60] mt-3">Help center</div>
          <p className="text-sm text-[#2C352D] mt-1 leading-relaxed">Browse our <a href="/how-it-works" className="text-[#D97757] hover:underline">step-by-step guide</a> or <a href="/faq" className="text-[#D97757] hover:underline">FAQ</a> first — most questions are answered there.</p>
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-serif-display text-3xl tracking-tight">Send a message</h2>
        {sent ? (
          <div data-testid="contact-sent" className="mt-6 bg-[#0F766E]/8 border border-[#0F766E]/25 rounded-2xl p-8 text-center">
            <p className="font-serif-display text-2xl text-[#0F766E]">Thanks — we got it.</p>
            <p className="text-sm text-[#2C352D] mt-3">We'll reply to <span className="font-semibold">{form.email}</span> within 1 business day.</p>
            <Button data-testid="contact-send-another" onClick={()=>{setSent(false); setForm({ name: "", email: "", subject: "", message: "" });}} variant="outline" className="rounded-full mt-6">Send another</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 bg-white p-8 rounded-2xl border border-[#E8E2D6] space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em]">Your name</Label>
                <Input data-testid="contact-name" required value={form.name} onChange={upd("name")} className={f} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em]">Email</Label>
                <Input data-testid="contact-email" type="email" required value={form.email} onChange={upd("email")} className={f} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em]">Subject</Label>
              <Input data-testid="contact-subject" required value={form.subject} onChange={upd("subject")} className={f} placeholder="Question about my listing" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em]">Message</Label>
              <Textarea data-testid="contact-message" required rows={6} value={form.message} onChange={upd("message")} className="mt-2 bg-[#F9F7F3] border-[#E8E2D6] rounded-xl" placeholder="Tell us what's going on…" />
            </div>
            <Button data-testid="contact-submit" disabled={loading} type="submit" className="w-full h-12 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white">
              {loading ? "Sending…" : "Send message"}
            </Button>
            <p className="text-xs text-[#5F6C60] text-center">By submitting, you agree to our <a href="/privacy" className="text-[#D97757] hover:underline">Privacy Policy</a>.</p>
          </form>
        )}
      </div>
    </div>
  );
}
